import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Space, 
  Tag, 
  Tooltip, 
  message, 
  Divider,
  Row,
  Col,
  Typography,
  Popconfirm,
  Collapse,
  Empty,
  Switch
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  LinkOutlined,
  ApiOutlined,
  SettingOutlined,
  EyeOutlined,
  CalendarOutlined,
  SyncOutlined,
  HistoryOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Panel } = Collapse;
const { TextArea } = Input;

const API_BASE_URL = '/rest/api';

interface ICalLink {
  source: string[];
  uri: string[];
}

interface ChannelmanagerSettings {
  dirs_api_token?: string;
  dirs_object_id?: string;
  secra_username?: string;
  secra_password?: string;
  secra_object_id?: string;
}

interface Advert {
  uid: number;
  title: string;
  shorttitle: string;
  status: number;
  ical_links?: string; // JSON string
  dirs_api_token?: string;
  dirs_object_id?: string;
  secra_username?: string;
  secra_password?: string;
  secra_object_id?: string;
}

export const Channelmanager: React.FC = () => {
  const [adverts, setAdverts] = useState<Advert[]>([]);
  const [loading, setLoading] = useState(false);
  const [icalModalVisible, setIcalModalVisible] = useState(false);
  const [settingsModalVisible, setSettingsModalVisible] = useState(false);
  const [currentAdvert, setCurrentAdvert] = useState<Advert | null>(null);
  const [icalForm] = Form.useForm();
  const [settingsForm] = Form.useForm();
  const [exportLinksModalVisible, setExportLinksModalVisible] = useState(false);
  const [currentExportLinks, setCurrentExportLinks] = useState<any[]>([]);
  const [syncStatusData, setSyncStatusData] = useState<any[]>([]);
  const [syncHistoryModalVisible, setSyncHistoryModalVisible] = useState(false);
  const [syncSettingsModalVisible, setSyncSettingsModalVisible] = useState(false);
  const [currentSyncHistory, setCurrentSyncHistory] = useState<any[]>([]);
  const [syncSettingsForm] = Form.useForm();

  // Lade alle Adverts
  const loadAdverts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}?tx_buchungsmanager_frontend[action]=getAdverts`, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setAdverts(result.data);
        } else {
          console.error('Failed to load adverts:', result.error);
          message.error('Fehler beim Laden der Objekte');
        }
      } else {
        console.error('Failed to load adverts:', response.status);
        message.error('Fehler beim Laden der Objekte');
      }
    } catch (error) {
      console.error('Error loading adverts:', error);
      message.error('Fehler beim Laden der Objekte');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAdverts();
    loadSyncStatus();
  }, []);

  // Lade Synchronisations-Status
  const loadSyncStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}?tx_buchungsmanager_frontend[action]=getSyncStatus`, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setSyncStatusData(result.data);
        } else {
          console.error('Failed to load sync status:', result.error);
        }
      } else {
        console.error('Failed to load sync status:', response.status);
      }
    } catch (error) {
      console.error('Error loading sync status:', error);
    }
  };

  // Parse iCal-Links
  const parseICalLinks = (icalLinksJson?: string): ICalLink[] => {
    if (!icalLinksJson) return [];
    try {
      return JSON.parse(icalLinksJson) || [];
    } catch {
      return [];
    }
  };

  // Öffne iCal-Links Modal
  const openICalModal = (advert: Advert) => {
    setCurrentAdvert(advert);
    const icalLinks = parseICalLinks(advert.ical_links);
    
    // Form mit aktuellen Werten füllen
    const formValues = icalLinks.map((link, index) => ({
      [`source_${index}`]: link.source.join(', '),
      [`uri_${index}`]: link.uri.join(', ')
    }));
    
    icalForm.setFieldsValue({
      icalLinks: icalLinks.length > 0 ? icalLinks : [{ source: [''], uri: [''] }]
    });
    setIcalModalVisible(true);
  };

  // Öffne Settings Modal  
  const openSettingsModal = (advert: Advert) => {
    setCurrentAdvert(advert);
    settingsForm.setFieldsValue({
      dirs_api_token: advert.dirs_api_token || '',
      dirs_object_id: advert.dirs_object_id || '',
      secra_username: advert.secra_username || '',
      secra_password: advert.secra_password || '',
      secra_object_id: advert.secra_object_id || ''
    });
    setSettingsModalVisible(true);
  };

  // Speichere iCal-Links
  const saveICalLinks = async () => {
    if (!currentAdvert) return;
    
    try {
      const values = await icalForm.validateFields();
      const icalLinks = values.icalLinks.map((link: any) => ({
        source: link.source.split(',').map((s: string) => s.trim()).filter(Boolean),
        uri: link.uri.split(',').map((u: string) => u.trim()).filter(Boolean)
      }));

      const response = await fetch(`${API_BASE_URL}?tx_buchungsmanager_frontend[action]=updateAdvertChannelmanager`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          advertId: currentAdvert.uid,
          ical_links: JSON.stringify(icalLinks)
        }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          message.success('iCal-Links erfolgreich gespeichert');
          setIcalModalVisible(false);
          loadAdverts(); // Reload für aktuelle Daten
        } else {
          message.error('Fehler beim Speichern: ' + result.error);
        }
      } else {
        message.error('Fehler beim Speichern der iCal-Links');
      }
    } catch (error) {
      console.error('Error saving iCal links:', error);
      message.error('Fehler beim Speichern der iCal-Links');
    }
  };

  // Speichere Channelmanager-Einstellungen
  const saveSettings = async () => {
    if (!currentAdvert) return;
    
    try {
      const values = await settingsForm.validateFields();
      
      const response = await fetch(`${API_BASE_URL}?tx_buchungsmanager_frontend[action]=updateAdvertChannelmanager`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          advertId: currentAdvert.uid,
          ...values
        }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          message.success('Channelmanager-Einstellungen erfolgreich gespeichert');
          setSettingsModalVisible(false);
          loadAdverts(); // Reload für aktuelle Daten
        } else {
          message.error('Fehler beim Speichern: ' + result.error);
        }
      } else {
        message.error('Fehler beim Speichern der Einstellungen');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      message.error('Fehler beim Speichern der Einstellungen');
    }
  };

  // Generiere iCal Export-URL (Legacy für Backwards Compatibility)
  const getICalExportUrl = (advert: Advert, source?: string) => {
    const baseUrl = window.location.origin;
    const params = new URLSearchParams({
      advert: advert.uid.toString()
    });
    if (source) {
      params.append('source', source);
    }
    return `${baseUrl}/ical/export?${params.toString()}`;
  };

  // Kopiere URL in Zwischenablage
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success('URL in Zwischenablage kopiert');
    }).catch(() => {
      message.error('Fehler beim Kopieren in die Zwischenablage');
    });
  };

  // Generiere Portal-spezifische Export-Links
  const generateExportLinks = async (advert: Advert) => {
    try {
      const response = await fetch(`${API_BASE_URL}?tx_buchungsmanager_frontend[action]=generateICalExportLinks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          advertId: advert.uid
        }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setCurrentAdvert(advert);
          setCurrentExportLinks(result.data.export_links);
          setExportLinksModalVisible(true);
        } else {
          message.error('Fehler beim Generieren der Export-Links: ' + result.error);
        }
      } else {
        message.error('Fehler beim Generieren der Export-Links');
      }
    } catch (error) {
      console.error('Error generating export links:', error);
      message.error('Fehler beim Generieren der Export-Links');
    }
  };

  // Triggert manuelle Synchronisation
  const triggerManualSync = async (advertId: number) => {
    try {
      message.loading('Synchronisation wird gestartet...', 0);

      const response = await fetch(`${API_BASE_URL}?tx_buchungsmanager_frontend[action]=triggerManualSync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          advertId: advertId
        }),
      });

      message.destroy();

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          message.success('Synchronisation erfolgreich abgeschlossen');
          loadSyncStatus(); // Reload Status
        } else {
          message.error('Fehler bei der Synchronisation: ' + result.error);
        }
      } else {
        message.error('Fehler bei der Synchronisation');
      }
    } catch (error) {
      message.destroy();
      console.error('Error triggering sync:', error);
      message.error('Fehler bei der Synchronisation');
    }
  };

  // Lädt Synchronisations-Historie
  const loadSyncHistory = async (advertId?: number) => {
    try {
      const params = new URLSearchParams();
      if (advertId) {
        params.append('advertId', advertId.toString());
      }
      params.append('limit', '50');

      const response = await fetch(`${API_BASE_URL}?tx_buchungsmanager_frontend[action]=getSyncHistory&${params.toString()}`, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setCurrentSyncHistory(result.data);
          setSyncHistoryModalVisible(true);
        } else {
          message.error('Fehler beim Laden der Sync-Historie: ' + result.error);
        }
      } else {
        message.error('Fehler beim Laden der Sync-Historie');
      }
    } catch (error) {
      console.error('Error loading sync history:', error);
      message.error('Fehler beim Laden der Sync-Historie');
    }
  };

  // Öffnet Sync-Einstellungen Modal
  const openSyncSettings = (advert: Advert) => {
    const syncData = syncStatusData.find(s => s.advert_id === advert.uid);
    setCurrentAdvert(advert);
    
    syncSettingsForm.setFieldsValue({
      auto_sync_enabled: syncData?.auto_sync_enabled ?? true,
      sync_interval_hours: syncData?.sync_interval_hours ?? 24,
      notification_email: syncData?.notification_email ?? '',
      notification_enabled: syncData?.notification_enabled ?? true
    });
    
    setSyncSettingsModalVisible(true);
  };

  // Speichert Sync-Einstellungen
  const saveSyncSettings = async () => {
    if (!currentAdvert) return;
    
    try {
      const values = await syncSettingsForm.validateFields();
      
      const response = await fetch(`${API_BASE_URL}?tx_buchungsmanager_frontend[action]=updateSyncSettings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          advertId: currentAdvert.uid,
          ...values
        }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          message.success('Sync-Einstellungen erfolgreich gespeichert');
          setSyncSettingsModalVisible(false);
          loadSyncStatus(); // Reload Status
        } else {
          message.error('Fehler beim Speichern: ' + result.error);
        }
      } else {
        message.error('Fehler beim Speichern der Sync-Einstellungen');
      }
    } catch (error) {
      console.error('Error saving sync settings:', error);
      message.error('Fehler beim Speichern der Sync-Einstellungen');
    }
  };

  // Tabellen-Spalten
  const columns = [
    {
      title: 'Objekt',
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: Advert) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.shorttitle} (ID: {record.uid})
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: number) => (
        <Tag color={status === 1 ? 'green' : 'red'}>
          {status === 1 ? 'Aktiv' : 'Inaktiv'}
        </Tag>
      ),
    },
    {
      title: 'iCal-Links',
      key: 'ical_links',
      render: (_: any, record: Advert) => {
        const icalLinks = parseICalLinks(record.ical_links);
        return (
          <div>
            {icalLinks.length > 0 ? (
              <Space direction="vertical" size="small">
                {icalLinks.map((link, index) => (
                  <div key={index}>
                    <Tag color="blue">{link.source.join(', ')}</Tag>
                    <Text code style={{ fontSize: '11px' }}>
                      {link.uri.join(', ').substring(0, 30)}...
                    </Text>
                  </div>
                ))}
              </Space>
            ) : (
              <Text type="secondary">Keine Links konfiguriert</Text>
            )}
          </div>
        );
      },
    },
    {
      title: 'Channelmanager',
      key: 'channelmanager',
      render: (_: any, record: Advert) => {
        const hasChannel = record.dirs_api_token || record.secra_username;
        return hasChannel ? (
          <Space direction="vertical" size="small">
            {record.dirs_api_token && (
              <Tag color="purple">DIRS21</Tag>
            )}
            {record.secra_username && (
              <Tag color="orange">SECRA</Tag>
            )}
          </Space>
        ) : (
          <Text type="secondary">Nicht konfiguriert</Text>
        );
      },
    },
    {
      title: 'Export-URLs',
      key: 'export_urls',
      render: (_: any, record: Advert) => {
        const icalLinks = parseICalLinks(record.ical_links);
        const hasLinks = icalLinks.length > 0;
        
        return (
          <Tooltip title={hasLinks ? "Portal-spezifische Export-URLs generieren" : "Erst iCal-Links konfigurieren"}>
            <Button
              type="text"
              icon={<LinkOutlined />}
              disabled={!hasLinks}
              onClick={() => generateExportLinks(record)}
            />
          </Tooltip>
        );
      },
    },
    {
      title: 'Sync-Status',
      key: 'sync_status',
      render: (_: any, record: Advert) => {
        const syncData = syncStatusData.find(s => s.advert_id === record.uid);
        if (!syncData) {
          return <Text type="secondary">Keine Daten</Text>;
        }

        const getStatusIcon = (status: string) => {
          switch (status) {
            case 'success':
              return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
            case 'error':
              return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
            case 'partial_error':
              return <ExclamationCircleOutlined style={{ color: '#faad14' }} />;
            default:
              return <ClockCircleOutlined style={{ color: '#d9d9d9' }} />;
          }
        };

        const formatDate = (timestamp: number) => {
          if (!timestamp) return 'Nie';
          return new Date(timestamp * 1000).toLocaleString();
        };

        const isOverdue = syncData.sync_overdue;

        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {getStatusIcon(syncData.last_import_status)}
              <Text style={{ fontSize: '12px' }}>
                {syncData.last_import_status === 'success' 
                  ? `${syncData.last_import_count} Buchungen`
                  : syncData.last_import_status || 'Unbekannt'
                }
              </Text>
            </div>
            <div style={{ fontSize: '11px', color: '#666' }}>
              Letzter Import: {formatDate(syncData.last_import_time)}
            </div>
            {isOverdue && (
              <div style={{ fontSize: '11px', color: '#ff4d4f' }}>
                Überfällig
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Aktionen',
      key: 'actions',
      render: (_: any, record: Advert) => {
        const syncData = syncStatusData.find(s => s.advert_id === record.uid);
        const hasICalLinks = parseICalLinks(record.ical_links).length > 0;
        
        return (
          <Space>
            <Tooltip title="iCal-Links verwalten">
              <Button
                type="text"
                icon={<CalendarOutlined />}
                onClick={() => openICalModal(record)}
              />
            </Tooltip>
            <Tooltip title="Channelmanager-Einstellungen">
              <Button
                type="text"
                icon={<SettingOutlined />}
                onClick={() => openSettingsModal(record)}
              />
            </Tooltip>
            <Tooltip title="Sync-Einstellungen">
              <Button
                type="text"
                icon={<ClockCircleOutlined />}
                onClick={() => openSyncSettings(record)}
              />
            </Tooltip>
            <Tooltip title={hasICalLinks ? "Manuelle Synchronisation" : "Erst iCal-Links konfigurieren"}>
              <Button
                type="text"
                icon={<SyncOutlined />}
                disabled={!hasICalLinks}
                onClick={() => triggerManualSync(record.uid)}
              />
            </Tooltip>
            <Tooltip title="Sync-Historie anzeigen">
              <Button
                type="text"
                icon={<HistoryOutlined />}
                onClick={() => loadSyncHistory(record.uid)}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>
          <ApiOutlined style={{ marginRight: '8px' }} />
          Channelmanager
        </Title>
        <Text type="secondary">
          Verwalten Sie iCal-Links und Channelmanager-Integrationen für Ihre Objekte
        </Text>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={adverts}
          loading={loading}
          rowKey="uid"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} von ${total} Objekten`,
          }}
        />
      </Card>

      {/* iCal-Links Modal */}
      <Modal
        title={`iCal-Links verwalten - ${currentAdvert?.title}`}
        open={icalModalVisible}
        onCancel={() => setIcalModalVisible(false)}
        onOk={saveICalLinks}
        width={800}
        okText="Speichern"
        cancelText="Abbrechen"
      >
        <Form form={icalForm} layout="vertical">
          <Form.List name="icalLinks">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Card key={key} size="small" style={{ marginBottom: '16px' }}>
                    <Row gutter={16}>
                      <Col span={10}>
                        <Form.Item
                          {...restField}
                          name={[name, 'source']}
                          label="Quelle(n)"
                          rules={[{ required: true, message: 'Quelle erforderlich!' }]}
                        >
                          <Input
                            placeholder="z.B. booking.com, airbnb.com"
                            addonBefore="Quelle:"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'uri']}
                          label="iCal-URL(s)"
                          rules={[{ required: true, message: 'URL erforderlich!' }]}
                        >
                          <Input
                            placeholder="https://ical.booking.com/v1/export?t=..."
                            addonBefore="URL:"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={2}>
                        <Form.Item label=" ">
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => remove(name)}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                >
                  Neuen iCal-Link hinzufügen
                </Button>
              </>
            )}
          </Form.List>
          
          <Divider />
          
          <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <Text strong>Export-URLs für dieses Objekt:</Text>
              <Button
                type="primary"
                size="small"
                icon={<LinkOutlined />}
                onClick={() => currentAdvert && generateExportLinks(currentAdvert)}
              >
                Portal-URLs generieren
              </Button>
            </div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Generieren Sie sichere, Portal-spezifische Export-URLs nach dem Speichern der iCal-Links
            </Text>
          </div>
        </Form>
      </Modal>

      {/* Channelmanager-Einstellungen Modal */}
      <Modal
        title={`Channelmanager-Einstellungen - ${currentAdvert?.title}`}
        open={settingsModalVisible}
        onCancel={() => setSettingsModalVisible(false)}
        onOk={saveSettings}
        width={600}
        okText="Speichern"
        cancelText="Abbrechen"
      >
        <Form form={settingsForm} layout="vertical">
          <Collapse defaultActiveKey={['dirs21']}>
            <Panel header="DIRS21 Integration" key="dirs21">
              <Form.Item
                name="dirs_api_token"
                label="API Token"
                extra="Ihr DIRS21 API-Token für die Preissynchronisation"
              >
                <Input.Password placeholder="Geben Sie Ihren DIRS21 API-Token ein" />
              </Form.Item>
              
              <Form.Item
                name="dirs_object_id"
                label="Objekt-ID"
                extra="Die DIRS21 Objekt-ID für dieses Inserat"
              >
                <Input placeholder="z.B. 12345" />
              </Form.Item>
            </Panel>
            
            <Panel header="SECRA Integration" key="secra">
              <Form.Item
                name="secra_username"
                label="Benutzername"
                extra="Ihr SECRA Benutzername für die API-Verbindung"
              >
                <Input placeholder="Ihr SECRA Benutzername" />
              </Form.Item>
              
              <Form.Item
                name="secra_password"
                label="Passwort"
                extra="Ihr SECRA Passwort für die API-Verbindung"
              >
                <Input.Password placeholder="Ihr SECRA Passwort" />
              </Form.Item>
              
              <Form.Item
                name="secra_object_id"
                label="Objekt-ID"
                extra="Die SECRA Objekt-ID für dieses Inserat"
              >
                <Input placeholder="z.B. 67890" />
              </Form.Item>
            </Panel>
          </Collapse>
        </Form>
      </Modal>

      {/* Export-Links Modal */}
      <Modal
        title={`Export-URLs - ${currentAdvert?.title}`}
        open={exportLinksModalVisible}
        onCancel={() => setExportLinksModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setExportLinksModalVisible(false)}>
            Schließen
          </Button>
        ]}
        width={800}
      >
        <div style={{ marginBottom: '16px' }}>
          <Text type="secondary">
            Portal-spezifische iCal Export-URLs. Jeder Link schließt Buchungen des jeweiligen Portals aus.
          </Text>
        </div>
        
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {currentExportLinks.map((link, index) => (
            <Card key={index} size="small">
              <Row gutter={16} align="middle">
                <Col span={4}>
                  <Tag color={link.portal === 'general' ? 'green' : 'blue'}>
                    {link.portal === 'general' ? 'Alle Buchungen' : link.portal}
                  </Tag>
                </Col>
                <Col span={16}>
                  <Input
                    value={link.url}
                    readOnly
                    size="small"
                    style={{ fontSize: '12px' }}
                  />
                </Col>
                <Col span={2}>
                  <Tooltip title="URL kopieren">
                    <Button
                      type="text"
                      icon={<LinkOutlined />}
                      size="small"
                      onClick={() => copyToClipboard(link.url)}
                    />
                  </Tooltip>
                </Col>
                <Col span={2}>
                  <Tooltip title="Download">
                    <Button
                      type="text"
                      icon={<EyeOutlined />}
                      size="small"
                      onClick={() => window.open(link.url, '_blank')}
                    />
                  </Tooltip>
                </Col>
              </Row>
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                {link.description}
              </div>
            </Card>
          ))}
        </Space>
        
        <Divider />
        
        <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '6px' }}>
          <Text strong>💡 Hinweise:</Text>
          <ul style={{ marginTop: '8px', marginBottom: 0, fontSize: '12px' }}>
            <li>Jeder Export-Link ist durch einen sicheren Token geschützt</li>
            <li>Links sind 30 Tage gültig und können bei Bedarf neu generiert werden</li>
            <li>Verwenden Sie die Portal-spezifischen Links in externen Systemen</li>
            <li>Der "Alle Buchungen" Link enthält alle Buchungen ohne Ausschluss</li>
          </ul>
        </div>
      </Modal>

      {/* Sync-Einstellungen Modal */}
      <Modal
        title={`Sync-Einstellungen - ${currentAdvert?.title}`}
        open={syncSettingsModalVisible}
        onCancel={() => setSyncSettingsModalVisible(false)}
        onOk={saveSyncSettings}
        width={600}
        okText="Speichern"
        cancelText="Abbrechen"
      >
        <Form form={syncSettingsForm} layout="vertical">
          <Form.Item
            name="auto_sync_enabled"
            label="Automatische Synchronisation"
            valuePropName="checked"
          >
            <Switch checkedChildren="Ein" unCheckedChildren="Aus" />
          </Form.Item>
          
          <Form.Item
            name="sync_interval_hours"
            label="Synchronisations-Intervall (Stunden)"
            rules={[
              { required: true, message: 'Intervall ist erforderlich' },
              { type: 'number', min: 1, max: 168, message: 'Zwischen 1 und 168 Stunden' }
            ]}
          >
            <Input
              type="number"
              min={1}
              max={168}
              addonAfter="Stunden"
              placeholder="24"
            />
          </Form.Item>
          
          <Divider />
          
          <Form.Item
            name="notification_enabled"
            label="Benachrichtigungen bei Fehlern"
            valuePropName="checked"
          >
            <Switch checkedChildren="Ein" unCheckedChildren="Aus" />
          </Form.Item>
          
          <Form.Item
            name="notification_email"
            label="E-Mail für Benachrichtigungen"
            rules={[
              { type: 'email', message: 'Gültige E-Mail-Adresse erforderlich' }
            ]}
          >
            <Input placeholder="ihre@email.de" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Sync-Historie Modal */}
      <Modal
        title="Synchronisations-Historie"
        open={syncHistoryModalVisible}
        onCancel={() => setSyncHistoryModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setSyncHistoryModalVisible(false)}>
            Schließen
          </Button>
        ]}
        width={1200}
      >
        <Table
          dataSource={currentSyncHistory}
          rowKey="uid"
          size="small"
          scroll={{ x: 800 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} von ${total} Einträgen`,
          }}
          columns={[
            {
              title: 'Zeit',
              dataIndex: 'import_time',
              width: 140,
              render: (timestamp: number) => (
                <Text style={{ fontSize: '12px' }}>
                  {new Date(timestamp * 1000).toLocaleString()}
                </Text>
              ),
            },
            {
              title: 'Portal',
              dataIndex: 'source',
              width: 100,
              render: (source: string) => (
                <Tag color="blue" style={{ fontSize: '11px' }}>{source}</Tag>
              ),
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 100,
              render: (status: string, record: any) => {
                const color = status === 'success' ? 'green' : 'red';
                return (
                  <div>
                    <Tag color={color} style={{ fontSize: '11px' }}>{status}</Tag>
                    {record.http_code && (
                      <div style={{ fontSize: '10px', color: '#666' }}>
                        HTTP {record.http_code}
                      </div>
                    )}
                  </div>
                );
              },
            },
            {
              title: 'Buchungen',
              key: 'bookings',
              width: 120,
              render: (_: any, record: any) => (
                <div style={{ fontSize: '11px' }}>
                  <div>✅ {record.bookings_imported} importiert</div>
                  <div>🔄 {record.bookings_updated} aktualisiert</div>
                  <div>⏭️ {record.bookings_skipped} übersprungen</div>
                </div>
              ),
            },
            {
              title: 'Performance',
              key: 'performance',
              width: 120,
              render: (_: any, record: any) => (
                <div style={{ fontSize: '11px' }}>
                  <div>⏱️ {record.execution_time_ms}ms</div>
                  <div>📊 {record.data_size_kb}KB</div>
                </div>
              ),
            },
            {
              title: 'Fehler',
              dataIndex: 'error_message',
              ellipsis: true,
              render: (error: string) => (
                error ? (
                  <Tooltip title={error}>
                    <Text type="danger" style={{ fontSize: '11px' }}>
                      {error.substring(0, 50)}...
                    </Text>
                  </Tooltip>
                ) : (
                  <Text type="secondary" style={{ fontSize: '11px' }}>-</Text>
                )
              ),
            },
          ]}
        />
      </Modal>
    </div>
  );
}; 